/**
 * Copyright (c) HashiCorp, Inc.
 * SPDX-License-Identifier: BUSL-1.1
 */

import './horizontal-bar-chart.scss';
import Component from '@glimmer/component';

import {
  HdsCardContainer,
  HdsSeparator,
  HdsTextBody,
  HdsApplicationState,
  HdsLinkStandalone,
} from '@hashicorp/design-system-components/components';
import TitleRow from './base/title-row.gts';

import LinealFluid from '@lineal-viz/lineal/components/lineal/fluid/index.js';
import LinealHBars from '@lineal-viz/lineal/components/lineal/h-bars/index.js';
import scaleLinear from '@lineal-viz/lineal/helpers/scale-linear.js';
import scaleBand from '@lineal-viz/lineal/helpers/scale-band.js';
import stackH from '@lineal-viz/lineal/helpers/stack-h.js';
import LinealAxis from '@lineal-viz/lineal/components/lineal/axis/index.js';
import axisOffset from '../../modifiers/axis-offset.ts';
import { tracked } from '@glimmer/tracking';
import type { SimpleDatum } from '../../types/index.ts';
import type { HdsApplicationStateSignature } from '@hashicorp/design-system-components/components/hds/application-state/index';

export interface SSUReportingHorizontalBarChartSignature {
  Args: {
    data: SimpleDatum[];
    title: string;
    description?: string;
    /** Custom text for the link (defaults to "View all") */
    linkText?: string;
    /** Icon to display with the link (defaults to "arrow-right") */
    linkIcon?: HdsLinkStandalone['icon'];
    /** URL for the link - if not provided, no link will be shown */
    linkUrl?: string;
    /** Target for the link - defaults to "_self" */
    linkTarget?: '_blank' | '_self';
  };
  Blocks: {
    default: [];
    /** We optionally yield application state to allow for overrides on empty state eg:
     * <SSUReportingHorizontalBarChart ...>
     *   <:empty as |A|>
     *     <A.Header @title="Custom Title" />
     *     <A.Body @text="Custom description" />
     *   </:empty>
     * </SSUReportingHorizontalBarChart>
     * */
    empty: HdsApplicationStateSignature['Blocks']['default'];
  };

  Element: HTMLElement;
}
export default class SSUReportingHorizontalBarChart extends Component<SSUReportingHorizontalBarChartSignature> {
  @tracked xRangeOffsetWidth = 0;

  get hasData() {
    return (
      this.args.data &&
      Array.isArray(this.args.data) &&
      this.args.data.length > 0
    );
  }

  get data() {
    if (!this.hasData) {
      return [];
    }
    // Filtering DESC for now per designs, could make configurable if needed
    return this.args.data
      .filter(({ value }) => value !== 0)
      .sort((a, b) => {
        return b.value - a.value;
      });
  }

  get total() {
    return this.data.reduce((runningTotal, { value }) => {
      return runningTotal + value;
    }, 0);
  }

  get a11yLabel() {
    const title = `Total of ${this.total} ${this.args.title}.`;

    const itemsDescription = this.data
      .map(({ value, label }) => {
        return `${value} ${label}`;
      })
      .join(', ');

    return `${title} Comprised of ${itemsDescription}.`;
  }

  get yDomain() {
    return this.data.map(({ label }) => label);
  }

  get xDomain() {
    return [0, Math.max(0, ...this.data.map(({ value }) => value))];
  }

  get rangeHeight() {
    return this.data.length * 26;
  }

  get yRange() {
    return [0, this.rangeHeight];
  }

  get emptyStateTitle() {
    return 'None enabled';
  }

  get emptyStateDescription() {
    const entitiesTitle = this.args.title;
    return `${entitiesTitle} in this namespace will appear here.`;
  }

  get emptyStateLinkText() {
    const entities = this.args.title.toLowerCase();
    return `Enable ${entities}`;
  }

  get description() {
    if (this.hasData) {
      return this.args.description;
    }
  }

  get linkUrl() {
    if (this.hasData) {
      return this.args.linkUrl;
    }
  }

  getXRange = (width: number) => {
    return [0, Math.max(0, width - this.xRangeOffsetWidth - 32)];
  };

  handleAxisOffset = (offsetWidth: number) => {
    this.xRangeOffsetWidth = offsetWidth;
  };

  <template>
    <HdsCardContainer
      ...attributes
      class="ssu-horizontal-bar-chart__container"
      @hasBorder={{true}}
    >
      <TitleRow
        @title={{@title}}
        @description={{this.description}}
        @linkUrl={{this.linkUrl}}
        @linkText={{@linkText}}
        @linkTarget={{@linkTarget}}
        @linkIcon={{@linkIcon}}
      />
      {{#if this.hasData}}
        {{! TODO: Figure out glint errors on lineal components }}
        {{! @glint-expect-error }}
        <LinealFluid class="ssu-horizontal-bar-chart__chart" as |width|>
          <svg
            height={{this.rangeHeight}}
            width="100%"
            {{axisOffset this.handleAxisOffset 8}}
            data-test-vault-reporting-horizontal-bar-chart-svg
          >
            {{! We are using the stacked version of the HBars as there seems to be an issue in the non-stacked version for how the x position is calculated.  }}
            {{#let
              (scaleLinear range=(this.getXRange width) domain=this.xDomain)
              (scaleBand range=this.yRange domain=this.yDomain)
              (stackH data=this.data x="value" y="label" z="")
              as |xScale yScale stacked|
            }}
              {{#if xScale.isValid}}
                <LinealAxis
                  @scale={{yScale}}
                  {{! @glint-expect-error }}
                  @orientation="left"
                  @includeDomain={{false}}
                />
                {{! TODO: Extra wrapper exists only for test attribute, figure out a better way }}
                <g data-test-vault-reporting-horizontal-bar-chart-bars>
                  <LinealHBars
                    @data={{stacked.data}}
                    {{! @glint-expect-error }}
                    @x="x"
                    {{! @glint-expect-error }}
                    @y="y"
                    {{! @glint-expect-error }}
                    @height={{6}}
                    @xScale={{xScale}}
                    @yScale={{yScale}}
                  />
                </g>
                <g>
                  {{! @glint-expect-error }}
                  {{#each stacked.data as |dataset|}}
                    {{#each dataset as |datum|}}
                      <text
                        class="ssu-horizontal-bar-chart__label"
                        {{! @glint-expect-error }}
                        y={{yScale.compute datum.y}}
                        x={{xScale.compute datum.x}}
                        dy="17.5px"
                        dx="8px"
                        data-test-vault-reporting-horizontal-bar-chart-inline-count
                        aria-label="{{datum.y}} {{datum.x}}"
                      >
                        {{datum.x}}
                      </text>
                    {{/each}}
                  {{/each}}
                </g>
              {{/if}}
            {{/let}}
          </svg>
        </LinealFluid>
        <HdsSeparator
          class="ssu-horizontal-bar-chart__separator"
          @spacing="0"
        />
        <HdsTextBody
          class="ssu-horizontal-bar-chart__total"
          @size="200"
          @tag="p"
          data-test-vault-reporting-horizontal-bar-chart-total
        >
          Total:
          {{this.total}}
        </HdsTextBody>
      {{else}}

        <HdsApplicationState
          data-test-vault-reporting-horizontal-bar-chart-empty-state
          class="ssu-horizontal-bar-chart__empty-state"
          as |A|
        >
          {{#if (has-block "empty")}}
            {{yield A to="empty"}}
          {{else}}
            <A.Header
              data-test-vault-reporting-horizontal-bar-chart-empty-state-title
              @title={{this.emptyStateTitle}}
            />
            <A.Body
              data-test-vault-reporting-horizontal-bar-chart-empty-state-description
              @text={{this.emptyStateDescription}}
            />
            {{#if @linkUrl}}
              <A.Footer as |F|>
                <F.LinkStandalone
                  data-test-vault-reporting-horizontal-bar-chart-empty-state-link
                  @icon="plus"
                  @text={{this.emptyStateLinkText}}
                  @href={{@linkUrl}}
                />
              </A.Footer>
            {{/if}}
          {{/if}}
        </HdsApplicationState>
      {{/if}}
    </HdsCardContainer>
  </template>
}
